<template>
	<action-panel :close="closeAction" :icon="callIcon" :links="panelLinks" :style="colorStyle" :class="{'action-panel-ringing-call':isRinging}" :buttons="panelButtons" @action="doAction" @close="doAction('done')">
		<div class="spaced" style="margin-bottom:4px">
			<div class="ap-icon-text"><sym-icon name="clock"></sym-icon><span><span class="fi">{{ panel.data.start }}</span></span></div>
			<div class="ap-icon-text" :style="{ opacity: hasDuration ? 1 : 0.5}">
				<sym-icon name="stopwatch"></sym-icon>
				<span>
					<span v-if="!hasDuration" class="fi">--:--</span>
					<seconds-timer v-else :run="panel.data.state === 'CONNECTED'" size="normal" v-model="panel.data.duration" :created="panel.created"></seconds-timer>
				</span>
			</div>
		</div>
		<action-comment v-if="hasDuration && !onContactPage && !contactTouched" :panel-id="panel.id" :autosave="true" @submit="doAction('done')" :comment="panel.data.comment"></action-comment>
	</action-panel>
</template>

<script setup lang="ts">
import {ref, computed, type CSSProperties} from "vue";
import ActionPanel from "@/ui/action-panel/ActionPanel.vue";
import SymIcon from "@fd/helpers/SymIcon.vue";
import {ActionPanelButton, ActionPanelLink, PbxPanelInstance} from "@t/ui/ActionPanel.ts";
import {SrcType} from "@t/meta/SourceType.ts";
import {injectEnv} from "@fd/Environment.ts";
import SecondsTimer from "@/ui/action-panel/SecondsTimer.vue";
import ActionComment from "@/ui/action-panel/ActionComment.vue";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import {go} from "@/router/Router.ts";

const props = defineProps<{
	panel: PbxPanelInstance
}>();
const env = injectEnv();
const ns = useNavigationStore();

const closeAction = computed<"no" | "cross" | "check">(() => {
	return props.panel.data.state === "END" ? "cross" : "no";
});

const hasDuration = computed<boolean>(() => props.panel.data.duration != null);
const onContactPage = computed<boolean>(() => {
	return ns.isOnDisplay(SrcType.CONTACT, props.panel.data.contact);
});
const contactTouched = computed<boolean>(() => {
	return props.panel.data.touched;
});

const callIcon = computed<ActionPanelLink>(() => {
	const icon:ActionPanelLink = { icon: "phone", srcType:SrcType.CONTACT, srcId: props.panel.data.contact };

	switch (props.panel.data.state) {
		case "RINGING":
			icon.icon = "call-ring";
			break;
		case "CONNECTED":
			icon.icon = props.panel.data.direction === "INCOMING" ? "call-in" : "call-out";
			break;
		case "END":
			icon.icon = props.panel.data.duration ? "phone" : "call-drop";
			break;
	}
	return icon;
});

const isRinging = computed<boolean>(() => props.panel.data.state === "RINGING");

const panelLinks = computed<ActionPanelLink[]>(() => {
	const linx:ActionPanelLink[] = [];
	if (Array.isArray(props.panel.data.links) && props.panel.data.links.length > 0) {
		linx.push(...props.panel.data.links);
	} else {
		linx.push({ title: 'call', srcType: SrcType.CONTACT, srcId: props.panel.data.contact });
	}
	return linx;
});

const colorStyle = computed<CSSProperties>(() => {
	const s: CSSProperties = {};
	let hue:number;
	let sat:number = 80;
	switch (props.panel.data.state) {
		case "RINGING":
			hue = 30;
			break;
		case "CONNECTED":
			hue = 120;
			break;
		case "END":
			hue = 204;
			if (!props.panel.data.duration) sat = 0;
			break;
	}
	s['--button-hue'] = hue;
	s['--button-sat'] = sat;
	return s;
});

const panelButtons = computed<ActionPanelButton[]>(() => {
	const dp:ActionPanelButton[] = [];
	if (props.panel.data.state === "END") {
		dp.push({ title: env.msg('butt.plan.call'), action: "plan-call", icon: "call" });
		dp.push({ title: env.msg('butt.call.back'), action: "call-back", icon: "call-back" });
	}
	return dp;
});

function doAction(action:string, arg?:any) {
	if (action === "done") {
		// Closed by the cross button or by submitting comment
		if (props.panel.data.state === "END") {
			ns.panelAction(props.panel.id, "done", {}, { comment: props.panel.data.comment });
		}
	} else if (action === "plan-call") {
		// close current call and redirect to make a new comm
		ns.panelAction(props.panel.id, "done").then(() => {
			let link = 'createContact.do';
			link = env.net.urlParam(link, "parentContact", props.panel.data.contact);
			link = env.net.urlParam(link, "days", 1);
			link = env.net.urlParam(link, "contactType", 1);
			link = env.net.urlParam(link, "replanContact", "true");
			go(link);
		});
	} else {
		ns.panelAction(props.panel.id, action);
	}
}

</script>

<style>
.action-panel-ringing-call {
	& .ap-icon {
		animation: ringing-pulse 1.2s ease-in-out infinite;
		transform-origin: center center;
	}
}

@keyframes ringing-pulse {
	0%, 100% {
		transform: scale(1);
		opacity: 1;
	}
	50% {
		transform: scale(1.5);
		opacity: 0.75;
	}
}
</style>